import React from 'react';
import moment from 'moment';

interface DateProps {
  date: any;
}

interface DateRangeProps {
  from?: any | null;
  to?: any | null;
}

// Explicit Europe/Vienna, not the viewer's own browser zone: these format calendar dates
// (arrival/departure, request dates) that carry no meaningful time-of-day, and must read the
// same for every admin regardless of where they are — matching what the booker picked.
const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: 'Europe/Vienna' });
const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'medium',
});
const dateTimeFormatShort = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

// Bare YYYY-MM-DD calendar-date strings (SM-2778) must be parsed as UTC, not moment's default
// local-browser-timezone interpretation of a plain date string — otherwise the calendar day
// shown here can shift by one for an admin whose browser timezone differs.
function _toDateForDisplay(date: any): Date {
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return new Date(date);
  return moment(date).toDate();
}

/**
 * Formats date using Intl.DateTimeFormat with medium date style.
 */
export function formatDate(date: any) {
  if (date) return dateFormat.format(_toDateForDisplay(date));
  else return null;
}

/**
 * React component for displaying formatted date.
 */
export function FormatDate(props: DateProps) {
  const { date } = props;

  if (date) return <>{dateFormat.format(_toDateForDisplay(date))}</>;
  else return null;
}

/**
 * Displays date range with '*' for missing dates.
 */
export function FormatDateRange(props: DateRangeProps) {
  const { from, to } = props;

  return (
    <>
      {from ? <FormatDate date={from} /> : '*'} - {to ? <FormatDate date={to} /> : '*'}
    </>
  );
}

/**
 * Formats date and time with medium style.
 */
export function FormatDateTime(props: DateProps) {
  const { date } = props;
  if (date) return <>{dateTimeFormat.format(moment(date).toDate())}</>;
  else return null;
}

/**
 * Formats date and time with short time style.
 */
export function FormatDateTimeShort(props: DateProps) {
  const { date } = props;
  if (date) return <>{dateTimeFormatShort.format(moment(date).toDate())}</>;
  else return null;
}

/**
 * Displays relative time (e.g. "2 hours ago") using moment.fromNow().
 */
export function FormatFromNow(props: DateProps) {
  const { date } = props;
  if (date) return <>{moment(date).fromNow()}</>;
  else return null;
}
